import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from "react-router-dom";
import './App.css';

// MUI Components
import { Box } from '@mui/material';

// Providers
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ModalProvider } from './contexts/ModalContext';
import { ProtectedRoute } from './components/ProtectedRoute';

// Bars
import Navbar from './components/Navbar/Navbar';
import Sidebar from './components/SideBar/SideBar';

// Pages
import Account from './pages/Account/Account';
import AddItem from './pages/AddItem/AddItem';
import AddItemsBulk from './pages/AddItem/AddItemsBulk';
import Catalog from './pages/Catalog/Catalog';
import CsvUpload from './pages/CsvUpload/CsvUpload';
import CsvUploadsBulk from './pages/CsvUpload/CsvUploadsBulk';
import HomePage from './pages/Home/HomePage';
import Login from './pages/Login/Login';
import MarterBuyList from './pages/MarterBuyList/MarterBuyList';
import MarterBuyListsBulk from './pages/MarterBuyList/MarterBuyListsBulk';
import MobileForm from './components/ProcessComponents/MobileForm';
import Pricing from './pages/Pricing/Pricing';
import Reports from './pages/Reports/Reports';
import Shipment from './pages/Shipment/Shipment';
import LoginSuperUser from './pages/Login/LoginSuperUser';
import SuperUserDashboard from './pages/SuperUser/Dashboard';
import SuperUserBox from './pages/SuperUser/SuperUserBox';
import PricingSuperUser from './pages/Pricing/PricingSuperUser';
import { ReportProvider } from './contexts/ReportContext';
import { fetchWrapperNoAuth, SERVER } from './utils/helpers';
import CustomCircularProgress from './components/Loaders/CustomCircularProgress';
import ErrorModal from './components/Modals/ErrorModal';
import WalmartPage from './pages/WalmartPage/WalmartPage';

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ReportProvider>
          <AppContent />
        </ReportProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

function AppContent() {
  const { theme } = useTheme();
  const { user, password, isSubUser, saveKeys } = useAuth();
  const [sidebarWidth, setSidebarWidth] = useState(65);
  const [isMobile, setIsMobile] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navbarHeight = '65px';

  const boxStyle = {
    height: `calc(100vh - ${navbarHeight})`,
    marginLeft: !!user && !isMobile ? `${sidebarWidth}px` : 0,
    mt: navbarHeight,
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '10px',
      height: '10px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.background.primary,
      borderRadius: '15px',
      border: `1px solid ${theme.border.light}`,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.scrollbar,
      borderRadius: '15px',
      cursor: 'grab',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: theme.scrollbarHover,
    },
    '&::-webkit-scrollbar-button': {
      display: 'none',
    },
  }

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    const walmartAuth = async () => {
      // Check if walmartCallbackUri parameter exists
      if (urlParams.has('sellerId')) {
        const params = Object.fromEntries(urlParams.entries());

        const { code, sellerId, state } = params

        if (!code || !sellerId || state !== user) return;

        setLoadingAuth(true);

        try {
          const data = await fetchWrapperNoAuth(`${SERVER}/falcon_walmart_auth`, user, password, {
            method: "POST",
            body: {
              code: code,
              sellerId: sellerId
            }
          });
          saveKeys(data)
        } catch {
          setErrorMessage("Failed to connect to your walmart account")
          setOpenErrorModal(true);
        } finally {
          setLoadingAuth(false);
          const cleanUrl = window.location.origin + window.location.pathname; // remove all params from url
          window.history.replaceState({}, document.title, cleanUrl);
        }
      }
    }

    if (urlParams.has("a")) {
      localStorage.setItem("affiliate", urlParams.get('a'))
    }

    const checkIfMobile = () => {
      // You can adjust this breakpoint based on your needs
      setIsMobile(window.innerWidth <= 768);
    };

    // Set initial value
    checkIfMobile();

    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);

    // check walmart auth
    walmartAuth();

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  useEffect(() => {
    const handleWheel = (e) => {
      // Check if the event target (the element being scrolled over)
      // is an input of type number
      const target = e.target;
      if (target instanceof HTMLInputElement && target.type === "number") {
        e.preventDefault();
      }
    };

    // Attach listener in capture phase so it runs early
    document.addEventListener("wheel", handleWheel, { passive: false, capture: true });

    return () => {
      document.removeEventListener("wheel", handleWheel, { capture: true });
    };
  }, []);

  return (
    <BrowserRouter>
      <Box>
        <Navbar isMobile={isMobile} />
        {!!user && <Sidebar setWidth={setSidebarWidth} isMobile={isMobile} />}
        <Box sx={boxStyle}>
          {loadingAuth ? <>
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, }}>
              <CustomCircularProgress sx={{ color: theme.color.primary }} />
            </Box>
          </> : <>
            {isSubUser && <SuperUserBox />}
            <Routes>
              <Route path="/">
                <Route path="/" element={<HomePage />} />
                <Route path="login" element={<Login />} />
                <Route path="login-super-user" element={<LoginSuperUser />} />
                <Route path="super-user" element={<SuperUserDashboard />} />
                <Route path="pricing-super-user" element={<PricingSuperUser />} />
                <Route path="pricing" element={<Pricing />} />
                <Route path="account" element={<Account />} />
                <Route path="catalog" element={<ProtectedRoute><Catalog isMobile={isMobile} /></ProtectedRoute>} />
                <Route path="shipments" element={<ProtectedRoute><Shipment isMobile={isMobile} /></ProtectedRoute>} />
                <Route path="add-item" element={<ProtectedRoute><AddItem isMobile={isMobile} navbarHeight={navbarHeight} /></ProtectedRoute>} />
                <Route path="add-items-bulk" element={<ProtectedRoute><ModalProvider><AddItemsBulk isMobile={isMobile} navbarHeight={navbarHeight} /></ModalProvider></ProtectedRoute>} />
                <Route path="marter-buy-list" element={<ProtectedRoute><MarterBuyList isMobile={isMobile} navbarHeight={navbarHeight} /></ProtectedRoute>} />
                <Route path="marter-buy-lists-bulk" element={<ProtectedRoute><ModalProvider><MarterBuyListsBulk isMobile={isMobile} navbarHeight={navbarHeight} /></ModalProvider></ProtectedRoute>} />
                <Route path="csv-upload" element={<ProtectedRoute><CsvUpload isMobile={isMobile} navbarHeight={navbarHeight} /></ProtectedRoute>} />
                <Route path="csv-uploads-bulk" element={<ProtectedRoute><ModalProvider><CsvUploadsBulk isMobile={isMobile} navbarHeight={navbarHeight} /></ModalProvider></ProtectedRoute>} />
                {/* <Route path="reports/*" element={<ProtectedRoute><Reports isMobile={isMobile} /></ProtectedRoute>} /> */}
                <Route path="mobile-form" element={<ProtectedRoute><MobileForm isMobile={isMobile} /></ProtectedRoute>} />
                <Route path="walmart" element={<WalmartPage />} />

                <Route path="*" element={<div style={{ textAlign: "center", marginTop: "10vh", marginBottom: "70vh", }}>Page not found.</div>} />
              </Route>
            </Routes>
          </>}
        </Box>
        <ErrorModal open={openErrorModal} setOpen={setOpenErrorModal} message={errorMessage} />
      </Box>
    </BrowserRouter>
  );
}

export default App;
